/**
 * `TablePager` — the footer page control: a rows-per-page select, a "1–10 of 95" range
 * readout, and prev/next nav.
 *
 * Deliberately NOT a `DataTable` prop. The preset owns the pagination ENGINE and the
 * consumer owns the chrome (the folder's thinness contract — no built-in pager prop);
 * this lives in the DS rather than in an app so the paged tables across the apps render
 * one control instead of three divergent ones. Render it as a SIBLING below the table.
 *
 * It takes plain values, NOT the TanStack `table`. That is load-bearing: `table` is
 * referentially STABLE across renders (the instance is mutated in place), so a component
 * that reads `table.getState()` during render has no reactive dependency on what it
 * displays. Under React Compiler — on in `affiliates-web` — the memoized output is then
 * reused forever and the pager freezes on its first render: "0–0 of 0" beneath a table
 * full of rows. Plain props make the dependency real, and make this unit testable without
 * standing up an engine.
 *
 * i18n-free like the rest of this folder: every label is a prop with an English default.
 * Also load-bearing — `@gto/ui/pagination` calls `formatMessage` with no `defaultMessage`,
 * so an app that does not load `libs/i18n` (affiliates-web keeps its own catalogue) would
 * render raw message ids as accessible names. Hence prev/next `IconButton`s here rather
 * than the numbered `Pagination`, which also matches the arrows-not-numbers footer
 * DataGridPro shipped.
 */
import type { ReactNode } from 'react'

import { ChevronLeftIcon } from '@gto/icons-react/solid/chevron-left'
import { ChevronRightIcon } from '@gto/icons-react/solid/chevron-right'
import { cn } from '@gto/ui/cn'
import { IconButton } from '@gto/ui/icon-button'
import { Select } from '@gto/ui/select'
import { typography } from '@gto/ui/typography'

/** The `from`–`to` of `total` slice the readout describes; 1-based and inclusive. */
export type TablePagerRange = {
  from: number
  to: number
  total: number
}

export type TablePagerLabels = {
  /** Sits left of the rows-per-page select. Defaults to `Rows per page`. */
  rowsPerPage?: ReactNode
  /** Accessible name for the rows-per-page select. Defaults to `Rows per page`. */
  rowsPerPageAriaLabel?: string
  /** Renders the range readout. Defaults to `from–to of total`. */
  renderRange?: (range: TablePagerRange) => ReactNode
  /** Accessible name for the previous-page button. Defaults to `Previous page`. */
  previousPageAriaLabel?: string
  /** Accessible name for the next-page button. Defaults to `Next page`. */
  nextPageAriaLabel?: string
}

export type TablePagerProps = {
  /** Current page, 0-based — the engine's `pagination.pageIndex`. */
  pageIndex: number
  pageSize: number
  /**
   * Total matching rows across every page. On the client tier that is the row array's
   * length; under `manualPagination` it is the server's count, since the engine only ever
   * holds the page it was handed.
   */
  total: number
  onPageChange: (pageIndex: number) => void
  /**
   * Rows-per-page choices. Omit these — or `onPageSizeChange` — to hide the picker, for a
   * table whose page size the caller fixes.
   */
  pageSizeOptions?: number[]
  onPageSizeChange?: (pageSize: number) => void
  labels?: TablePagerLabels
  className?: string
}

const DEFAULT_ROWS_PER_PAGE_LABEL = 'Rows per page'

// `undefined` locale, not `'en-US'`: a DS primitive must not pin one locale's grouping. Apps
// that own a message catalogue pass `labels.renderRange` and never reach this.
const defaultRenderRange = ({ from, to, total }: TablePagerRange): ReactNode =>
  `${from.toLocaleString()}–${to.toLocaleString()} of ${total.toLocaleString()}`

export const TablePager = ({
  pageIndex,
  pageSize,
  total,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
  labels,
  className,
}: TablePagerProps) => {
  const rowsPerPageLabel = labels?.rowsPerPage ?? DEFAULT_ROWS_PER_PAGE_LABEL
  const renderRange = labels?.renderRange ?? defaultRenderRange

  const pageCount = pageSize > 0 ? Math.ceil(total / pageSize) : 0
  const from = total === 0 ? 0 : pageIndex * pageSize + 1
  const to = Math.min(total, (pageIndex + 1) * pageSize)
  const showPicker =
    pageSizeOptions !== undefined && pageSizeOptions.length > 0 && onPageSizeChange !== undefined

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-end gap-4 px-3 py-2 text-text-secondary',
        typography.body3,
        className,
      )}
    >
      {showPicker && (
        <div className="flex shrink-0 items-center gap-2">
          {/* The label must not wrap: as a flex item it shrinks to its longest word
              otherwise, stacking "Rows / per / page" into three lines. */}
          <span className="whitespace-nowrap">{rowsPerPageLabel}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(next) => {
              onPageSizeChange(Number(next))
            }}
          >
            <Select.Trigger
              aria-label={labels?.rowsPerPageAriaLabel ?? DEFAULT_ROWS_PER_PAGE_LABEL}
              size="small"
            >
              <Select.Value />
              <Select.Icon />
            </Select.Trigger>
            <Select.Dropdown>
              <Select.List>
                {pageSizeOptions.map((option) => (
                  <Select.Item
                    key={option}
                    value={String(option)}
                  >
                    {option}
                  </Select.Item>
                ))}
              </Select.List>
            </Select.Dropdown>
          </Select>
        </div>
      )}

      <span
        className="shrink-0 whitespace-nowrap"
        data-ui="table-pager-range"
      >
        {renderRange({ from, to, total })}
      </span>

      <div className="flex shrink-0 items-center">
        <IconButton
          aria-label={labels?.previousPageAriaLabel ?? 'Previous page'}
          disabled={pageIndex <= 0}
          size="small"
          onClick={() => onPageChange(pageIndex - 1)}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          aria-label={labels?.nextPageAriaLabel ?? 'Next page'}
          disabled={pageIndex + 1 >= pageCount}
          size="small"
          onClick={() => onPageChange(pageIndex + 1)}
        >
          <ChevronRightIcon />
        </IconButton>
      </div>
    </div>
  )
}
